import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ApiRequestStatus, GlobalActions } from '../../../model';
import appRoutes from '../../../routing/app-routes';
import {
  firstAccess,
  messagesForFirstAccessError,
  resetFirstAccessState,
  resetLoginState,
} from '../../../store';
import { useAppDispatch, useAppSelector } from '../../redux';
import commonErrorMessages from '../../../messages/common-messages/common-error.messages';

export interface FirstAccessLogicCallbacks {
  oldPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  firstAccessRequestStatus: ApiRequestStatus;

  setOldPassword: (string) => void;
  setNewPassword: (string) => void;
  setConfirmNewPassword: (string) => void;
  handleClose: () => void;
  handleSubmit: () => void;
}

export const useFirstAccess = (
  actions: GlobalActions
): FirstAccessLogicCallbacks => {
  const { navigate, showToast, closeToast } = actions;

  const [oldPassword, setOldPassword] = useState<string>();
  const [newPassword, setNewPassword] = useState<string>();
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>();

  const { firstAccessRequestStatus, firstAccessError } = useAppSelector(
    (state) => state.features.firstAccess
  );

  const { migrato } = useAppSelector((state) => state.features.login);
  const { username } = useAppSelector((state) => state.data.user);
  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();

  const handleClose = useCallback(() => {
    if (firstAccessRequestStatus === 'succeeded') {
      dispatch(resetFirstAccessState());
      dispatch(resetLoginState());
      navigate(appRoutes.Login, { replace: true });
    }
  }, [dispatch, firstAccessRequestStatus, navigate]);

  const handleSubmit = useCallback(() => {
    dispatch(
      firstAccess({
        username: username,
        oldPassword: oldPassword,
        newPassword: newPassword,
        newPasswordConfirm: confirmNewPassword,
        notMigrated: !migrato,
      })
    );
  }, [
    dispatch,
    username,
    oldPassword,
    newPassword,
    confirmNewPassword,
    migrato,
  ]);

  const [userNotified, setUserNotified] = useState(false);

  useEffect(() => {
    if (!migrato && !userNotified) {
      showToast({
        type: 'info',
        title: formatMessage(commonErrorMessages.errorTitleDefault),
        description: formatMessage(commonErrorMessages.errorMigratoDefault),
        close: closeToast,
      });
      setUserNotified(true);
    }
  }, [closeToast, formatMessage, migrato, showToast, userNotified]);

  useEffect(() => {
    if (firstAccessError) {
      const errorMessages = messagesForFirstAccessError(firstAccessError);
      showToast({
        type: 'error',
        title: formatMessage(errorMessages.title),
        description:
          errorMessages.description && formatMessage(errorMessages.description),
        close: closeToast,
      });
      dispatch(resetFirstAccessState());
    }
  }, [firstAccessError, dispatch, formatMessage, showToast, closeToast]);

  return {
    oldPassword,
    newPassword,
    confirmNewPassword,
    firstAccessRequestStatus,
    setConfirmNewPassword,
    setNewPassword,
    setOldPassword,
    handleClose,
    handleSubmit,
  };
};
